import { Controller, useForm } from 'react-hook-form'

import { zodResolver } from '@hookform/resolvers/zod'

import { useUpsertMediaAnalysisAutomationApi } from '@/common/api/modules/mediaAnalysisAutomation'
import { useAnalysisTargetListApi } from '@/common/api/modules/mediaAnalysisTarget'
import { ScheduleItem } from '@/common/api/params/mediaAnalysisAutomation'
import { analysisTargetPath } from '@/common/app/path'
import { useShowSavedToast } from '@/common/app/toast'
import { WidgetChoice } from '@/common/form/choice'

import Button, { ButtonColor } from '@/components/view/atoms/button/Button'
import TextButton from '@/components/view/atoms/button/TextButton'
import FormLabel from '@/components/view/atoms/form/FormLabel'
import Selector, { SelectorSize } from '@/components/view/atoms/selector/Selector'
import Text, { TextColor } from '@/components/view/atoms/text/Text'
import FormRow from '@/components/view/layouts/form/FormRow'
import ItemAlign from '@/components/view/layouts/item/ItemAlign'
import ModalContent from '@/components/view/layouts/modal/ModalContent'
import ModalFooter from '@/components/view/molecules/modal/ModalFooter'

import FormCheckBoxes from '@/components/container/form/widgets/FormCheckBoxes'
import FormError from '@/components/container/form/widgets/FormError'
import FormRadioButton from '@/components/container/form/widgets/FormRadioButton'
import FormSelector from '@/components/container/form/widgets/FormSelector'
import AnalysisTargetSummary from '@/components/container/modules/media/analysisTarget/AnalysisTargetSummary'

import {
  DAY_OF_MONTH_CHOICES,
  MediaAnalysisAutomationSettingsSchemaType,
  WEEKDAY_CHOICES,
  buildMediaAnalysisAutomationSettingsSchema,
  convertItemToSchema,
  convertSchemaToUpsertVars
} from './mediaAnalysisAutomationSettingsSchema'

const FREQUENCY_CHOICES: WidgetChoice<string | number>[] = [
  { name: '日次', value: 'daily' },
  { name: '週次', value: 'weekly' },
  { name: '月次', value: 'monthly' }
]

const HOUR_CHOICES: WidgetChoice<string | number>[] = Array.from({ length: 24 }, (_, h) => ({
  name: `${h}時`,
  value: h
}))

const MINUTE_CHOICES: WidgetChoice<string | number>[] = [0, 15, 30, 45].map((m) => ({
  name: `${m}分`,
  value: m
}))

interface Props {
  siteId: number
  item: ScheduleItem
  onSaved(): void
  onCancel(): void
  forEnabling: boolean
}

// 分析自動実行の設定フォーム（頻度・曜日/日・時刻・分析対象サイト名）
const MediaAnalysisAutomationSettingsForm = ({
  siteId,
  item,
  onSaved,
  onCancel,
  forEnabling
}: Props) => {
  const {
    register,
    handleSubmit,
    control,
    watch,
    formState: { errors }
  } = useForm<MediaAnalysisAutomationSettingsSchemaType>({
    resolver: zodResolver(buildMediaAnalysisAutomationSettingsSchema(item.requires_target)),
    defaultValues: convertItemToSchema(item)
  })

  const frequency = watch('frequency')
  const hour = watch('hour')
  const minute = watch('minute')

  const { data: targets } = useAnalysisTargetListApi({ site_id: siteId })
  const targetChoices: WidgetChoice<string | number>[] = (targets ?? []).map((t) => ({
    name: t.name,
    value: t.id
  }))
  const hasNoTarget = item.requires_target && targetChoices.length === 0

  const showSaved = useShowSavedToast({ editing: true })
  const { trigger: upsert, isMutating: saving } = useUpsertMediaAnalysisAutomationApi({
    onSuccess: () => {
      showSaved(item.label + ' 自動実行設定')
      onSaved()
    }
  })

  const handleSave = handleSubmit((data) =>
    upsert(convertSchemaToUpsertVars(siteId, item.key, data, forEnabling)).catch(() => undefined)
  )
  const selectedTargetId = watch('targetId')
  const target = (targets ?? []).find((t) => t.id === selectedTargetId)

  return (
    <>
      <ModalContent>
        <FormRow direction='vertical' gap={4}>
          <FormLabel fixWidth={null}>{item.label}</FormLabel>
          <Text size={12}>
            この分析を定期的に自動実行するための設定です。
            {forEnabling ? '保存すると自動実行がONになります。' : ''}
          </Text>
        </FormRow>
        <FormRow align='center'>
          <FormLabel>自動実行</FormLabel>
          <Text>{item.enabled ? 'ON' : 'OFF'}</Text>
        </FormRow>
        <FormRow>
          <FormLabel>実行頻度</FormLabel>
          <FormRadioButton
            control={control}
            name='frequency'
            choices={FREQUENCY_CHOICES}
            direction='horizon'
            gap={12}
          />
        </FormRow>

        {frequency === 'weekly' && (
          <FormRow>
            <FormLabel>実行曜日</FormLabel>
            <ItemAlign gapX={8}>
              <FormCheckBoxes control={control} name='weekdays' choices={WEEKDAY_CHOICES} />
            </ItemAlign>
            <FormError message={errors.weekdays?.message} />
          </FormRow>
        )}

        {frequency === 'monthly' && (
          <FormRow>
            <FormLabel>実行日</FormLabel>
            <Controller
              control={control}
              name='dayOfMonth'
              render={({ field }) => (
                <Selector
                  name={field.name}
                  size={SelectorSize.AUTO}
                  value={field.value ?? ''}
                  choices={DAY_OF_MONTH_CHOICES}
                  onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : null)}
                />
              )}
            />
            <FormError message={errors.dayOfMonth?.message} />
          </FormRow>
        )}

        <FormRow>
          <FormLabel>実行時刻</FormLabel>
          <ItemAlign gapX={8} align='center'>
            <FormSelector
              register={register('hour', { valueAsNumber: true })}
              value={hour}
              choices={HOUR_CHOICES}
            />
            <Text>:</Text>
            <FormSelector
              register={register('minute', { valueAsNumber: true })}
              value={minute}
              choices={MINUTE_CHOICES}
            />
          </ItemAlign>
        </FormRow>

        {item.requires_target && (
          <>
            <FormRow>
              <FormLabel>分析対象サイト名</FormLabel>
              {hasNoTarget ? (
                <Text size={12} color={TextColor.CAUTION}>
                  分析対象が登録されていません。先に「分析対象管理」から追加してください。
                </Text>
              ) : (
                <FormRow direction='vertical' gap={8}>
                  <FormRow align='center'>
                    <Controller
                      control={control}
                      name='targetId'
                      render={({ field }) => (
                        <Selector
                          name={field.name}
                          size={SelectorSize.AUTO}
                          value={field.value ?? ''}
                          choices={[{ name: '— 分析対象を選択 —', value: '' }, ...targetChoices]}
                          onChange={(e) =>
                            field.onChange(e.target.value ? Number(e.target.value) : null)
                          }
                        />
                      )}
                    />
                    <TextButton size={12} link={analysisTargetPath(siteId)} newTab>
                      分析対象管理
                    </TextButton>
                  </FormRow>
                  <FormError message={errors.targetId?.message} />
                  {target && <AnalysisTargetSummary target={target} />}
                </FormRow>
              )}
            </FormRow>
          </>
        )}
      </ModalContent>
      <ModalFooter>
        <Button color={ButtonColor.SECONDARY} size='large' onClick={onCancel}>
          キャンセル
        </Button>
        <Button
          color={ButtonColor.PRIMARY}
          size='large'
          loading={saving}
          disabled={hasNoTarget}
          onClick={handleSave}
        >
          {forEnabling ? '保存してONにする' : '設定を保存する'}
        </Button>
      </ModalFooter>
    </>
  )
}

export default MediaAnalysisAutomationSettingsForm
